"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AdPlaceholder } from "@/components/ad-placeholder";
import { renderWithHashtags } from "@/lib/utils/index";

interface Ad {
  id: string;
  campaignId: string;
  adType: string;
  title: string;
  description?: string;
  artworkUrl?: string;
  landingUrl: string;
  sponsorName: string;
  clearingCpm?: number;
}

function trackEvent(campaignId: string, event: "impression" | "click", clearingCpm?: number) {
  fetch("/api/ads/feed", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ campaignId, event, clearingCpm }),
  }).catch(() => {});
}

function useImpressionRef(ad: Ad) {
  return useCallback(
    (el: HTMLElement | null) => {
      if (!el || !ad.campaignId || (el as any).__adObserver) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            trackEvent(ad.campaignId, "impression", ad.clearingCpm);
            obs.disconnect();
            (el as any).__adObserver = null;
          }
        },
        { threshold: 0.5 }
      );
      obs.observe(el);
      (el as any).__adObserver = obs;
    },
    [ad.campaignId, ad.clearingCpm]
  );
}

// ─── Shared sponsored-ad video — matches components/FeedView.tsx ──────────
// Lazy-loaded (preload="none" + IntersectionObserver), no native controls so
// the iOS/macOS Safari "big play" glyph never appears. Ad playback is
// muted + looping + unclickable-past-the-landing-link by design.
function SponsoredAdVideo({ src, title }: { src: string; title?: string }) {
  const [errored, setErrored] = useState(false);
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) { setInView(true); io.disconnect(); break; }
        }
      },
      { rootMargin: "150px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !inView) return;
    const tryPlay = () => { el.play().catch(() => {}); };
    if (el.readyState >= 2) tryPlay();
    else el.addEventListener("canplay", tryPlay, { once: true });
    return () => el.removeEventListener("canplay", tryPlay);
  }, [inView]);

  if (errored) {
    return (
      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
        <span className="text-[10px] text-gray-400 font-medium">{title ?? "Sponsored"}</span>
      </div>
    );
  }

  return (
    <video
      ref={ref}
      src={inView ? src : undefined}
      autoPlay
      muted
      loop
      playsInline
      preload="none"
      controls={false}
      disablePictureInPicture
      controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
      onError={() => setErrored(true)}
      className="sponsored-ad-video w-full h-full object-cover"
    />
  );
}

// ─── Shared ad description — clickable hashtags + more/less toggle ────────
// Sponsored descriptions render inside an outer <a>. The hashtag buttons and
// the toggle both preventDefault() so clicking them never follows the
// landing URL.
function AdDescription({
  description,
  collapsedClamp = "line-clamp-2",
  textClassName = "text-xs text-gray-500 leading-snug",
  buttonClassName = "text-xs font-semibold text-blue-600 hover:underline",
}: {
  description: string;
  collapsedClamp?: string;
  textClassName?: string;
  buttonClassName?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const pRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    const el = pRef.current;
    if (!el || expanded) return;
    const check = () => setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [description, expanded, collapsedClamp]);

  const handleHashtagNavigate = useCallback((tag: string) => {
    const normalized = tag.replace(/^#/, "");
    window.location.href = `/hashtag/${encodeURIComponent(normalized)}`;
  }, []);

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  return (
    <div className={textClassName}>
      <p ref={pRef} className={expanded ? "" : collapsedClamp}>
        {renderWithHashtags(description, handleHashtagNavigate)}{" "}
        {expanded && (
          <button type="button" onClick={toggle} className={`${buttonClassName} ml-0.5`}>
            less
          </button>
        )}
      </p>
      {!expanded && isTruncated && (
        <button type="button" onClick={toggle} className={`${buttonClassName} mt-0.5`}>
          more
        </button>
      )}
    </div>
  );
}

// ─── Sidebar ad variant ──────────────────────────────────────────────────────
function SidebarAd({ ad }: { ad: Ad }) {
  const ref = useImpressionRef(ad);
  const isVideo = ad.adType === "VIDEO" && ad.artworkUrl?.match(/\.(mp4|webm|mov)$/i);
  const isImage = ad.artworkUrl && !isVideo;

  return (
    <a
      href={ad.landingUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={() => trackEvent(ad.campaignId, "click", ad.clearingCpm)}
      ref={ref as any}
      className="block bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden group"
    >
      {(isVideo || isImage) && (
        <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100">
          {isVideo ? (
            <SponsoredAdVideo src={ad.artworkUrl!} title={ad.title} />
          ) : (
            <img src={ad.artworkUrl!} alt={ad.title} loading="lazy" className="w-full h-full object-cover" />
          )}
          <span className="absolute top-2 right-2 text-[9px] text-white/90 border border-white/40 rounded px-1.5 py-0.5 bg-black/50 font-medium tracking-wide">
            Sponsored
          </span>
        </div>
      )}
      <div className="px-3 py-2.5 space-y-1">
        {!isVideo && !isImage && (
          <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide">Sponsored</span>
        )}
        <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug group-hover:text-blue-600 transition-colors">
          {ad.title}
        </p>
        {ad.description && (
          <AdDescription
            description={ad.description}
            collapsedClamp="line-clamp-2"
            textClassName="text-xs text-gray-500 leading-snug"
            buttonClassName="text-xs font-semibold text-blue-600 hover:underline"
          />
        )}
        <div className="flex items-center justify-between pt-0.5">
          <span className="text-[10px] text-gray-400 truncate">{ad.sponsorName}</span>
          <span className="text-[10px] font-semibold text-blue-600 shrink-0 ml-2 group-hover:underline">
            Learn more →
          </span>
        </div>
      </div>
    </a>
  );
}

// ─── Banner ad variant (full-width, landscape) ──────────────────────────────
// Rendered as a compact leaderboard strip: artwork capped at 120px tall so it
// doesn't dominate the page on contextual surfaces (Study Groups, Homepage).
function BannerAd({ ad }: { ad: Ad }) {
  const ref = useImpressionRef(ad);
  const isVideo = ad.adType === "VIDEO" && ad.artworkUrl?.match(/\.(mp4|webm|mov)$/i);
  const isImage = ad.artworkUrl && !isVideo;

  return (
    <a
      href={ad.landingUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={() => trackEvent(ad.campaignId, "click", ad.clearingCpm)}
      ref={ref as any}
      className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden group px-3 py-2"
    >
      {(isVideo || isImage) && (
        <div className="relative flex-shrink-0 w-[120px] h-[72px] overflow-hidden rounded-lg bg-gray-100">
          {isVideo ? (
            <SponsoredAdVideo src={ad.artworkUrl!} title={ad.title} />
          ) : (
            <img src={ad.artworkUrl!} alt={ad.title} loading="lazy" className="w-full h-full object-cover" />
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide border border-gray-200 rounded px-1 py-0.5">
            Sponsored
          </span>
          <span className="text-[10px] text-gray-400 truncate">{ad.sponsorName}</span>
        </div>
        <p className="text-sm font-semibold text-gray-900 line-clamp-1 leading-snug group-hover:text-blue-600 transition-colors">
          {ad.title}
        </p>
        {ad.description && (
          <AdDescription
            description={ad.description}
            collapsedClamp="line-clamp-1"
            textClassName="text-xs text-gray-500 leading-snug"
            buttonClassName="text-xs font-semibold text-blue-600 hover:underline"
          />
        )}
      </div>
      <span className="text-xs font-semibold text-blue-600 shrink-0 group-hover:underline whitespace-nowrap">
        Learn more →
      </span>
    </a>
  );
}

// ─── Inline ad variant (between forum topics / list items) ──────────────────
function InlineAd({ ad }: { ad: Ad }) {
  const ref = useImpressionRef(ad);
  const isVideo = ad.adType === "VIDEO" && ad.artworkUrl?.match(/\.(mp4|webm|mov)$/i);
  const isImage = ad.artworkUrl && !isVideo;

  return (
    <a
      href={ad.landingUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={() => trackEvent(ad.campaignId, "click", ad.clearingCpm)}
      ref={ref as any}
      className="flex items-start gap-3 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden p-3 group"
    >
      {(isVideo || isImage) && (
        <div className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
          {isVideo ? (
            <SponsoredAdVideo src={ad.artworkUrl!} title={ad.title} />
          ) : (
            <img src={ad.artworkUrl!} alt={ad.title} loading="lazy" className="w-full h-full object-cover" />
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide">Sponsored</span>
          <span className="text-[10px] text-gray-300">·</span>
          <span className="text-[10px] text-gray-400 truncate">{ad.sponsorName}</span>
        </div>
        <p className="text-sm font-semibold text-gray-900 line-clamp-1 leading-snug group-hover:text-blue-600 transition-colors">
          {ad.title}
        </p>
        {ad.description && (
          <AdDescription
            description={ad.description}
            collapsedClamp="line-clamp-1"
            textClassName="text-[11px] text-gray-500 leading-snug"
            buttonClassName="text-[11px] font-semibold text-blue-600 hover:underline"
          />
        )}
      </div>
      <span className="text-[10px] font-semibold text-blue-600 shrink-0 group-hover:underline hidden sm:block">
        Learn more →
      </span>
    </a>
  );
}

// ─── LiveAdSlot — fetches and renders a real campaign ad ─────────────────────
// Falls back to AdPlaceholder if no live campaign is available for this slot.
export type AdSlotVariant = "sidebar" | "banner" | "inline";

interface LiveAdSlotProps {
  placement: string;
  variant?: AdSlotVariant;
  className?: string;
  fallback?: boolean;
}

export function LiveAdSlot({
  placement,
  variant = "sidebar",
  className = "",
  fallback = true,
}: LiveAdSlotProps) {
  const [ad, setAd] = useState<Ad | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/ads/feed?placement=${encodeURIComponent(placement)}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const ads: Ad[] = data.ads ?? [];
        if (ads.length > 0) {
          setAd(ads[0]);
        }
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [placement]);

  if (!loaded) return null;

  if (!ad) {
    if (!fallback) return null;
    const placeholderType = variant === "banner" ? "banner" : variant === "inline" ? "sponsored-post" : "sidebar";
    return <AdPlaceholder type={placeholderType} className={className} />;
  }

  const Wrapper = ({ children }: { children: React.ReactNode }) =>
    className ? <div className={className}>{children}</div> : <>{children}</>;

  if (variant === "banner") return <Wrapper><BannerAd ad={ad} /></Wrapper>;
  if (variant === "inline") return <Wrapper><InlineAd ad={ad} /></Wrapper>;
  return <Wrapper><SidebarAd ad={ad} /></Wrapper>;
}
